import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { HotelSearchFilters } from '@bandido/shared/types/hotels/hotel-search-filters';
import type { HotelSearchTransport } from './hotelSearchFlow';
import type { HotelFare, HotelSummary } from './searchModel';
import { useHotelResultCollection } from './hotelResultCollection';
import { useHotelScrollAnchor } from './useHotelScrollAnchor';
import { HotelResults } from './HotelResults';

interface SourceProps {
  searchId: string;
  hotel: HotelSummary;
  filters: HotelSearchFilters;
  transport: HotelSearchTransport;
  polling: boolean;
}
interface SourceState {
  rows: HotelFare[];
  busy: boolean;
  partial: boolean;
  error: 'changed' | 'exhausted' | 'error' | null;
  hasMore: boolean;
  more: () => unknown;
  reload: () => unknown;
}
function FareSource({ onState, ...p }: SourceProps & { onState: (id: string, state: SourceState | null) => void }) {
  const c = useHotelResultCollection(
    `${p.searchId}:${p.hotel.hotel_id}:${JSON.stringify(p.filters)}`,
    (signal, cursor) => p.transport.hotelFares(p.searchId, p.hotel.hotel_id, signal, p.filters, cursor),
    p.polling,
    (row) => `${row.fare.search_id}:${row.fare.fare_id}`,
  );
  useEffect(() => {
    onState(p.searchId, {
      rows: c.page?.data.map((r) => r.fare) ?? [],
      busy: c.busy,
      partial: !!c.page?.meta.partial,
      error: c.error,
      hasMore: !!c.page?.meta.next_cursor,
      more: c.more,
      reload: c.reload,
    });
  }, [p.searchId, c.page, c.busy, c.error, onState]);
  useEffect(() => () => onState(p.searchId, null), [p.searchId, onState]);
  return null;
}
/** A single visual list, with independent revision-bound parent/child cursors and original fare provenance. */
export function HotelDirectOffers({
  detailId,
  detailPolling,
  onReserve,
  ...p
}: SourceProps & {
  detailId?: string | null;
  detailPolling: boolean;
  onReserve: (fare: HotelFare) => void;
}) {
  const { t } = useTranslation();
  const [loadedMore, setLoadedMore] = useState(false);
  const [sources, setSources] = useState<Record<string, SourceState | null>>({});
  const update = useCallback((id: string, state: SourceState | null) => setSources((s) => ({ ...s, [id]: state })), []);
  const active = [sources[p.searchId], p.searchId !== detailId && detailId ? sources[detailId] : null].filter(
    (s): s is SourceState => !!s,
  );
  const rows = active.flatMap((s) => s.rows);
  const busy = active.some((s) => s.busy);
  const errors = active.filter((s) => s.error && s.error !== 'changed');
  const root = useRef<HTMLDivElement>(null);
  useHotelScrollAnchor(root, sources, `${p.searchId}:${p.hotel.hotel_id}`);
  return (
    <Box ref={root}>
      <HotelResults fares={rows} hotels={[p.hotel]} onReserve={onReserve} direct />
      <Stack gap={1}>
        {busy && (
          <Typography role="status" variant="caption">
            {t('hotelSearch.loadingOffers')}
          </Typography>
        )}
        {active.some((s) => s.partial) && <Alert severity="warning">{t('hotelSearch.partial')}</Alert>}
        {errors.length > 0 && (
          <Alert severity="warning">
            {t(
              errors.some((s) => s.error === 'exhausted') ? 'hotelSearch.resultsRefreshStopped' : 'hotelSearch.failed',
            )}
            <Button disabled={busy} onClick={() => errors.forEach((s) => s.reload())}>
              {t('hotelSearch.reloadResults')}
            </Button>
          </Alert>
        )}
        {(loadedMore || active.some((s) => s.hasMore)) && (
          <Button
            aria-disabled={busy || !active.some((s) => s.hasMore)}
            onClick={() => {
              if (busy || !active.some((s) => s.hasMore)) return;
              setLoadedMore(true);
              active.filter((s) => s.hasMore).forEach((s) => s.more());
            }}
          >
            {t(active.some((s) => s.hasMore) ? 'hotelSearch.moreOffers' : 'hotelSearch.allReceivedOffers')}
          </Button>
        )}
        <FareSource {...p} onState={update} />
        {detailId && detailId !== p.searchId && (
          <FareSource {...p} searchId={detailId} polling={detailPolling} onState={update} />
        )}
      </Stack>
    </Box>
  );
}
